import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Container, Title, SecondStepContainer } from './AddBuyerModal.styled';
import { Modal } from '../Modal/Modal';
import { useTranslation } from 'react-i18next';
import ScrollableList from 'components/ScrollableList/ScrollableList';
import { ICompany } from 'types/Company.types';
import { useDebounce } from 'utils/hooks/useDebounce';
import { IAddBuyerDTO, useAddBuyer, useGetEligibleBuyers } from './hooks';
import { ELIGIBLE_BUYERS_PER_PAGE } from './constants';
import { getDataFromPages } from 'utils/getDataFromPages';
import Input from 'components/Input/Input';
import Button from 'components/Button/Button';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';

export interface IAddBuyerModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export function AddBuyerModal({ isOpen, setIsOpen }: IAddBuyerModalProps) {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [selectedBuyer, setSelectedBuyer] = useState<ICompany | null>(null);
  const debouncedSearchByCompany = useDebounce(searchBy);

  const { data: buyersData, isLoading: isLoadingGetBuyers } =
    useGetEligibleBuyers(
      ELIGIBLE_BUYERS_PER_PAGE,
      debouncedSearchByCompany,
      isOpen
    );

  const companies: ICompany[] = getDataFromPages(
    buyersData?.pages,
    'companies'
  );

  const { mutate: addBuyer, isLoading: isLoadingAddBuyer } = useAddBuyer();

  const handleSelect = (company: ICompany) => {
    setSelectedBuyer(company);
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedBuyer(null);
      setReference('');
      setSearchBy('');
    }
  }, [isOpen]);

  const { isSmallPhone } = useBreakpointFlag();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      modalStyle={{
        margin: 'auto',
        width: isSmallPhone ? '90%' : '600rem',
        minWidth: isSmallPhone ? '90%' : 'auto',
        position: 'fixed',
      }}
    >
      <Container>
        <Title>
          {!selectedBuyer
            ? t('Select buyer for adding')
            : t('Enter custom reference for {{COMPANY_NAME}}').replace(
                '{{COMPANY_NAME}}',
                selectedBuyer.name
              )}
        </Title>
        {!selectedBuyer ? (
          <ScrollableList
            items={companies || []}
            isLoading={isLoadingGetBuyers || isLoadingAddBuyer}
            noContentLabel={t('No buyers found')}
            onClick={(company: ICompany) => handleSelect(company)}
            onSearch={(str: string) => setSearchBy(str)}
          />
        ) : (
          <SecondStepContainer>
            <Input
              placeholder={t('Reference')}
              width={'300rem'}
              onChange={(e: any) => setReference(e.target.value)}
              disabled={isLoadingAddBuyer}
            />
            <Button
              primary
              label={t('Confirm')}
              disabled={!reference || !selectedBuyer || isLoadingAddBuyer}
              width="200rem"
              wrapperStyle={{ marginTop: '20rem' }}
              onClick={() => {
                const dto: IAddBuyerDTO = {
                  companyId: selectedBuyer.id,
                  reference,
                };
                addBuyer(dto, {
                  onSuccess: () => setIsOpen(false),
                });
              }}
            />
          </SecondStepContainer>
        )}
      </Container>
    </Modal>
  );
}
